import { format } from 'date-fns';
import { FC, KeyboardEventHandler } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
	BanOutline,
	Box,
	Button,
	Card,
	CardBody,
	CelebratePartyPopperOutline,
	Center,
	Display,
	Divider,
	DocumentDownloadOutline,
	DotsVerticalSolid,
	Flex,
	Heading,
	HStack,
	IconButton,
	Image,
	InformationCircleOutline,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	PencilAltOutline,
	rem,
	SimpleGrid,
	Skeleton,
	Spacer,
	Stack,
	SupportOutline,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	useDisclosure,
	VStack,
} from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';
import { useBuildRecognizeURL, useQueriesControl } from '@bonusly/url';
import { useAnalytics } from '@bonusly/who-am-i';

import { ReactComponent as CelebrationCircle } from '../../assets/images/celebration-circle.svg';
import { Conceal } from '../../components/display/conceal';
import { Paginator } from '../../components/navigation/paginator';
import { PageTitle } from '../../components/page/page-title';
import { SortableHeader } from '../../components/table/sortable-header';
import { Celebration, CelebrationType } from '../../entities/celebrations';
import { RuleGroup } from '../../entities/groups';
import { useCelebrations, useInactiveCelebrations, useInvalidateCelebrations } from '../../queries/celebrations';
import { Metadata } from '../../queries/meta';
import { trackClick } from '../../utils/analytics';
import birthdayURL from './assets/birthday-tile.png';
import customURL from './assets/custom-tile.png';
import newHireURL from './assets/new-hire-tile.png';
import welcomeURL from './assets/welcome-tile.png';
import workAnniversaryURL from './assets/work-anniversary-tile.png';
import { CELEBRATION_BLOG_URL, CELEBRATION_HELP_URL } from './constants';
import { DeactivateCelebrationModal } from './deactivate-celebration-modal';
import * as events from './events';
import { useCelebrationReportCSVExport } from './hooks';
import { SendManualModal } from './send-manual-modal';

const LoadingSkeleton = () => {
	return (
		<Td>
			<Box py={2}>
				<Skeleton w="full" h={2} />
			</Box>
		</Td>
	);
};

export const CelebrationsIndex = () => {
	const { t } = useTranslation('celebrations', { keyPrefix: 'admin' });
	const { handleExportClick, isLoading: exportLoading } = useCelebrationReportCSVExport();
	const newCelebrationModal = useDisclosure();

	// The useQueryControl hooks is fine if you're only setting a single query parameter at a time
	// but in this case we potentially need to set multiple query parameters at once
	const [queries, setQueries] = useQueriesControl(['page', 'sort_by', 'sort_direction'], {
		defaultValue: {
			page: '1',
			sort_by: 'name',
			sort_direction: 'ASC',
		},
		allowedValues: {
			sort_by: ['name', 'amount'],
			sort_direction: ['ASC', 'DESC'],
		},
	});

	const parsed = parseInt(queries.page, 10);
	const page = isNaN(parsed) ? 1 : Math.max(1, parsed);

	const { data: [records, meta] = [[], new Metadata()], isLoading } = useCelebrations({
		page_number: page,
		sort_by: queries.sort_by,
		sort_direction: queries.sort_direction,
	});

	const globalCelebrationExists = (type: CelebrationType) => {
		return records.some((celebration) => celebration.type === type && celebration.isGlobal);
	};

	return (
		<>
			<Stack px={10} py={8} spacing={6}>
				<PageTitle>{t('title')}</PageTitle>
				<HStack>
					<Display size="sm">{t('title')}</Display>
					<Spacer />
					<Button minW={rem(120)} onClick={newCelebrationModal.onOpen} {...trackClick('Open Create Celebration Modal')}>
						{t('create.button')}
					</Button>

					{!isLoading && records.length > 0 && (
						<Tooltip label={t('exportCsvLabel')}>
							<IconButton
								aria-label={t('exportCsvLabel')}
								onClick={handleExportClick}
								data-testid="celebration-export-csv-button"
								isDisabled={exportLoading}
								{...trackClick('Download Celebrations CSV')}
							>
								<DocumentDownloadOutline />
							</IconButton>
						</Tooltip>
					)}
				</HStack>

				<Card p={0} variant="flat" minW="fit-content" borderRadius="sm">
					<HStack gap={2} p={4} backgroundColor="gray.25" borderTopRadius="sm">
						<Text fontSize="inherit" maxW={720}>
							{t('indexDescription')}
						</Text>
					</HStack>

					<Table variant="simple">
						<Thead bg="bg.soft">
							<Tr>
								<Th minW={220}>
									<SortableHeader
										currentDirection={queries.sort_direction}
										currentField={queries.sort_by}
										field="name"
										onSort={(field, direction) => setQueries({ sort_by: field, sort_direction: direction })}
									>
										{t('nameLabel')}
									</SortableHeader>
								</Th>
								<Th minW={120}>
									<SortableHeader currentDirection={'ASC'} currentField={'period'} field="start_date" onSort={() => {}}>
										{t('periodLabel')}
									</SortableHeader>
								</Th>
								<Th minW={120}>
									<HStack gap={1} justify="flex-end" align="center">
										<SortableHeader
											currentDirection={queries.sort_direction}
											currentField={queries.sort_by}
											field="amount"
											onSort={(field, direction) => setQueries({ sort_by: field, sort_direction: direction })}
										>
											{t('pointLabel')}
										</SortableHeader>
									</HStack>
								</Th>
								<Th minW={120}>
									<HStack gap={1} justify="flex-start" align="center">
										<Text fontSize="inherit">{t('recipientLabel')}</Text>
									</HStack>
								</Th>
								<Th minW={120}>
									<HStack gap={1} justify="flex-start" align="center">
										<Text fontSize="inherit">{t('postTypeLabel')}</Text>
									</HStack>
								</Th>
								<Th></Th>
							</Tr>
						</Thead>

						<Tbody>
							{records.map((celebration) => (
								<CelebrationRow key={celebration.id} celebration={celebration} />
							))}

							{isLoading &&
								Array.from({ length: 5 }).map((_, index) => (
									<Tr key={index}>
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<Td></Td>
									</Tr>
								))}

							{!isLoading && records.length === 0 && (
								<Tr>
									<Td colSpan={8}>
										<Center p={8} flexDir="column" textAlign="center">
											<CelebrationCircle />
											<Trans i18nKey="admin.noCelebrations" t={t}>
												<Text as="h3" mt={6}>
													Choose from preconfigured celebrations like birthday or work anniversary.
												</Text>

												<Text as="h4" mt={2}>
													Create your own based on an important date to your company.{' '}
													<Text as={Link} color="text.interactive" to={CELEBRATION_BLOG_URL} target="_blank" rel="noopener noreferrer">
														Get ideas on our blog
													</Text>
													.
												</Text>
											</Trans>
										</Center>
									</Td>
								</Tr>
							)}
						</Tbody>
					</Table>

					{meta.totalPages > 1 && (
						<HStack p={5}>
							<Flex flexBasis={0} flexGrow={1}>
								{t('viewing', { count: records.length, total: meta.totalCount })}
							</Flex>
							<Paginator currentPage={page} totalPages={meta.totalPages} onSelectPage={(pageNum) => setQueries({ page: pageNum.toString() })} />
							<Flex flexBasis={0} flexGrow={1}></Flex>
						</HStack>
					)}
				</Card>

				<HStack gap={1} display="flex" align="center">
					<Tooltip>
						<Center>
							<InformationCircleOutline size="xs" />
						</Center>
					</Tooltip>
					<Text as="h4" fontWeight="medium" fontSize="sm" color="gray.500">
						<Trans i18nKey="admin.learnMoreText" t={t}>
							<Text
								as={Link}
								mt={2}
								fontWeight={500}
								fontSize={50}
								color="text.interactive"
								to={CELEBRATION_HELP_URL}
								target="_blank"
								rel="noopener noreferrer"
							>
								Learn more
							</Text>{' '}
							in our Help Center.
						</Trans>
					</Text>
				</HStack>
			</Stack>

			<Modal isOpen={newCelebrationModal.isOpen} onClose={newCelebrationModal.onClose} size="2xl">
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>{t('create.newCelebrationTitle')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody p={5}>
						<Stack spacing={4}>
							<Text>{t('create.newCelebrationDescription')}</Text>

							<SimpleGrid columns={2} gap={4}>
								<Conceal if={globalCelebrationExists(CelebrationType.Birthday)}>
									<CelebrationTemplateCard
										alt={t('create.birthday.alt')}
										color="red.100"
										description={t('create.birthday.description')}
										src={birthdayURL}
										title={t('create.birthday.title')}
										type={CelebrationType.Birthday}
									/>
								</Conceal>

								<Conceal if={globalCelebrationExists(CelebrationType.WorkAnniversary)}>
									<CelebrationTemplateCard
										alt={t('create.workAnniversary.alt')}
										color="purple.100"
										description={t('create.workAnniversary.description')}
										src={workAnniversaryURL}
										title={t('create.workAnniversary.title')}
										type={CelebrationType.WorkAnniversary}
									/>
								</Conceal>

								<Conceal if={globalCelebrationExists(CelebrationType.NewHire)}>
									<CelebrationTemplateCard
										alt={t('create.newHire.alt')}
										color="blue.100"
										description={t('create.newHire.description')}
										src={newHireURL}
										title={t('create.newHire.title')}
										type={CelebrationType.NewHire}
									/>
								</Conceal>

								<Conceal if={globalCelebrationExists(CelebrationType.Welcome)}>
									<CelebrationTemplateCard
										alt={t('create.welcome.alt')}
										color="green.100"
										description={t('create.welcome.description')}
										src={welcomeURL}
										title={t('create.welcome.title')}
										type={CelebrationType.Welcome}
									/>
								</Conceal>

								<CelebrationTemplateCard
									alt={t('create.custom.alt')}
									color="gray.100"
									description={t('create.custom.description')}
									src={customURL}
									title={t('create.custom.title')}
									type={CelebrationType.Custom}
								/>
							</SimpleGrid>
						</Stack>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
};

type CelebrationTemplateCardProps = {
	alt: string;
	color: string;
	description: string;
	src: string;
	title: string;
	type: string;
};

const CelebrationTemplateCard = ({ color, src, alt, title, type, description }: CelebrationTemplateCardProps) => {
	const { t } = useTranslation('celebrations', { keyPrefix: 'admin' });
	const { data } = useInactiveCelebrations();

	assertPresence(data);

	const inactive = data.find((celebration) => celebration.type === type);
	const path = inactive ? inactive.editPath : `/admin/programs/celebrations/new/${type}`;

	return (
		<Card
			as={Link}
			// FIXME: I don't know what's going on with this. The type is better represented as a query param instead of a URL param.
			// However, whenever you navigate with the query param something causes it to disappear immediately afterwards.
			// Ticket for this is SHARK-2331
			to={path}
			borderWidth="1px"
			borderStyle="solid"
			borderColor="border.default"
			boxShadow="none"
			overflow="hidden"
		>
			<CardBody p={0} role="group">
				<Center pos="relative" bgColor={color} px={rem(23)} py={rem(34)}>
					<Image src={src} alt={alt} />
					<Box pos="absolute" bg="white" px={3} py={2} borderRadius="sm" bgColor="blue.500" color="white" display="none" _groupHover={{ display: 'block' }}>
						{t('create.newCelebrationButton')}
					</Box>
				</Center>

				<Stack px={5} py={4}>
					<Heading>{title}</Heading>
					<Text>{description}</Text>
				</Stack>
			</CardBody>
		</Card>
	);
};

type CelebrationRowProps = {
	celebration: Celebration;
};

export const CelebrationRow: FC<CelebrationRowProps> = ({ celebration }) => {
	const { t } = useTranslation('celebrations', { keyPrefix: 'admin' });
	const navigate = useNavigate();
	const deactivateModal = useDisclosure();
	const sendManualModal = useDisclosure();
	const invalidateCelebrations = useInvalidateCelebrations();
	const recognizeURL = useBuildRecognizeURL();
	const { track } = useAnalytics();
	// NOTE: This navigates to the edit page right now but can be updated to go to a
	// detail page if we go that route
	const detailPath = `/admin/programs/celebrations/${celebration.id}/edit`;
	const reviewPath = recognizeURL(`/company/users/${celebration.giverId}`);

	const handleViewCelebration = () => {
		track(...events.view(celebration));
		navigate(detailPath);
	};

	const handleKeyDown: KeyboardEventHandler<HTMLTableRowElement> = (e) => {
		if (e.key === 'Enter') {
			handleViewCelebration();
		}
	};

	const getCelebrationFrequencyDisplay = (type: CelebrationType, celebrationDate?: Date): string => {
		switch (type) {
			case CelebrationType.Birthday:
				return t('frequency.birthday');
			case CelebrationType.Welcome:
				return t('frequency.welcome');
			case CelebrationType.WorkAnniversary:
				return t('frequency.workAnniversary');
			case CelebrationType.Wellness:
				return t('frequency.wellness');
			case CelebrationType.NewHire:
				return t('frequency.newHire');
			default:
				return celebrationDate ? format(celebrationDate, 'MMM d') : t('frequency.custom');
		}
	};

	const getAudienceDisplay = (receivingRulesGroup: RuleGroup | null): string => {
		if (!receivingRulesGroup) {
			return t('everyone');
		}

		return receivingRulesGroup.conditions.map((condition) => condition.value).join(', ');
	};

	const getPostTypeDisplay = (): string => {
		if (!celebration.isCustom) {
			return t('individual');
		} else {
			return t('group');
		}
	};

	return (
		<>
			<Tr
				cursor="pointer"
				data-testid={`celebration-${celebration.id}`}
				key={celebration.id}
				onClick={() => navigate(detailPath)}
				onKeyDown={handleKeyDown}
				tabIndex={0}
				transitionProperty="background-color"
				transitionDuration="fast"
				_hover={{
					bg: 'secondary.bgAlpha',
				}}
				{...trackClick(...events.view(celebration))}
				{...(celebration.yearAmountMappings.length > 0 && {
					sx: {
						td: {
							borderBottom: 'none',
						},
					},
				})}
			>
				<Td fontWeight="semibold">{celebration.name}</Td>
				<Td>{getCelebrationFrequencyDisplay(celebration.type, celebration.celebrationDate || undefined)}</Td>
				<Td textAlign="end">{celebration.amount}</Td>
				<Td>{getAudienceDisplay(celebration.receivingRulesGroup)}</Td>
				<Td>{getPostTypeDisplay()}</Td>
				<Td textAlign="end">
					<Menu>
						<MenuButton as={IconButton} aria-label={'Menu Label'} onClick={(e) => e.stopPropagation()} variant="ghost">
							<DotsVerticalSolid size="sm" />
						</MenuButton>
						<MenuList width={240}>
							<MenuItem
								as={Link}
								to={reviewPath}
								onClick={(e) => {
									track(...events.review(celebration));
									e.stopPropagation();
								}}
								py={3}
							>
								<HStack gap={1}>
									<CelebratePartyPopperOutline />
									<Text>{t('reviewLabel')}</Text>
								</HStack>
							</MenuItem>

							<MenuItem
								onClick={(e) => {
									sendManualModal.onOpen();
									e.stopPropagation();
								}}
								py={3}
							>
								<HStack gap={1}>
									<SupportOutline />
									<Text>{t('sendManualLabel')}</Text>
								</HStack>
							</MenuItem>

							<Divider borderColor="grayAlpha.300" borderWidth="0.5px" my={rem(1)} />

							<Conceal if={celebration.isWellness || celebration.isCustom}>
								<MenuItem
									as={Link}
									to={celebration.editPath}
									onClick={(e) => {
										handleViewCelebration();
										e.stopPropagation();
									}}
									py={3}
								>
									<HStack align="start" gap={1}>
										<PencilAltOutline />
										<VStack align="start" gap={0}>
											<Text>{t('editLabel')}</Text>
										</VStack>
									</HStack>
								</MenuItem>
							</Conceal>

							<MenuItem
								onClick={(e) => {
									e.stopPropagation();
									deactivateModal.onOpen();
								}}
								py={3}
							>
								<HStack gap={1}>
									<BanOutline />
									<Text>{t('deactivateLabel')}</Text>
								</HStack>
							</MenuItem>
						</MenuList>
					</Menu>
				</Td>
			</Tr>

			{celebration.yearAmountMappings.map((mapping, index) => {
				return (
					<Tr
						key={index}
						border="none"
						onClick={() => navigate(detailPath)}
						_hover={{
							bg: 'secondary.bgAlpha',
						}}
						_notLast={{
							td: {
								borderBottom: 'none',
							},
						}}
						sx={{
							td: {
								borderTop: 'none',
							},
						}}
					>
						<Td pl={8} fontWeight="normal"></Td>
						<Td>{t('yearMilestone', { year: mapping.yearThreshold })}</Td>
						<Td textAlign="end">{mapping.amount}</Td>
						<Td>{getAudienceDisplay(celebration.receivingRulesGroup)}</Td>
						<Td>{getPostTypeDisplay()}</Td>
						<Td></Td>
					</Tr>
				);
			})}

			<DeactivateCelebrationModal celebration={celebration} disclosure={deactivateModal} onSuccess={() => invalidateCelebrations()} redirectPath="" />
			<SendManualModal celebration={celebration} {...sendManualModal} />
		</>
	);
};
